import { Icon, ButtonField } from '@wk/components-v3-react16';
import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import styles from './DeleteModal.module.scss';
import { collectAffectedIdsByDeletion } from '../../../../workflowTreeUtils';
import { ServerStepGroup } from '../../../../ServerWorkflowType';

interface DeleteModalProperties {
  isOpen: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  position: { x: number; y: number } | null;
  serverStepGroup: ServerStepGroup | null;
  targetId: number | null;
}

const DeleteModal = ({
  isOpen,
  onCancel,
  onConfirm,
  serverStepGroup,
  targetId,
  position,
}: DeleteModalProperties) => {
  const modalReference = useRef<HTMLDivElement>(null);
  const affectedItems = collectAffectedIdsByDeletion(serverStepGroup, targetId);
  const totalStepsToDelete = affectedItems.workflowRemovableStepIds.length;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        modalReference.current &&
        !modalReference.current.contains(event.target as Node)
      ) {
        onCancel();
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onCancel();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, onCancel]);

  if (!isOpen || !position) {
    return null;
  }

  const modalContent = (
    <>
      <div className={styles.overlay} />
      <div
        ref={modalReference}
        className={styles.deleteModalWrapper}
        style={{
          left: `${position.x}px`,
          top: `${position.y}px`,
        }}
        data-testid="delete-modal"
      >
        <div className={styles.headerWrapper}>
          <h3 id="delete-modal-header" className={styles.headerText}>
            Are you sure you want to delete the highlighted modules (
            {totalStepsToDelete})?
          </h3>
          <div className="header-buttons">
            <ButtonField type="icon" size="large">
              <button type="button" onClick={onCancel} flow-id="close-button">
                <Icon name="wk-icon-close" />
                <span className={styles.srOnly}>Close modal</span>
              </button>
            </ButtonField>
          </div>
        </div>

        <div className={styles.bodyText}>This action cannot be undone.</div>

        <div className={styles.buttons}>
          <ButtonField type="text">
            <button
              type="button"
              onClick={onCancel}
              flow-id="cancel-delete-button"
            >
              Cancel
            </button>
          </ButtonField>
          <ButtonField type="primary" color="danger">
            <button
              type="button"
              onClick={onConfirm}
              flow-id="confirm-delete-button"
            >
              Delete
            </button>
          </ButtonField>
        </div>
      </div>
    </>
  );

  const rootElement = document.querySelector('#root');
  return rootElement ? createPortal(modalContent, rootElement) : null;
};

export default DeleteModal;
